/**
 * WebSocket event types matching the ChatConsumer EventType constants.
 * These types ensure type safety when handling WebSocket messages.
 */

// ============================================================================
// Event Type Constants
// ============================================================================

/** Client -> Server event types */
export const ClientEventType = {
  USER_MESSAGE: 'user_message',
  TOOL_RESPONSE: 'tool_response',
  PING: 'ping',
  RESTORE_SESSION: 'restore_session',
  UPDATE_LANGUAGE: 'update_language',
  SELECT_LOCATION: 'select_location',
  SUBMIT_PRECHAT_FORM: 'submit_prechat_form',
} as const;

/** Server -> Client event types */
export const ServerEventType = {
  SESSION_READY: 'session_ready',
  SESSION_RESTORED: 'session_restored',
  CHAT_SETTINGS: 'chat_settings',
  STREAMING_CHUNK: 'streaming_chunk',
  STREAM_COMPLETE: 'stream_complete',
  NEW_MESSAGE: 'new_message',
  LANGUAGE_UPDATE: 'language_update',
  PONG: 'pong',
  ERROR: 'error',
  WHATSAPP_HANDOFF: 'whatsapp_handoff',
} as const;

export type ClientEventTypeValue = (typeof ClientEventType)[keyof typeof ClientEventType];
export type ServerEventTypeValue = (typeof ServerEventType)[keyof typeof ServerEventType];

// ============================================================================
// Client -> Server Event Payloads
// ============================================================================

export interface UserMessageEvent {
  type: typeof ClientEventType.USER_MESSAGE;
  message: string;
}

export interface ToolResponseEvent {
  type: typeof ClientEventType.TOOL_RESPONSE;
  tool_call_id: string;
  tool_name: string;
  result: Record<string, unknown>;
}

export interface PingEvent {
  type: typeof ClientEventType.PING;
  timestamp?: number;
}

export interface RestoreSessionEvent {
  type: typeof ClientEventType.RESTORE_SESSION;
  session_id: string;
}

export interface UpdateLanguageEvent {
  type: typeof ClientEventType.UPDATE_LANGUAGE;
  language: string;
}

/** Scopes the conversation to the location the visitor picked when the widget opened. */
export interface SelectLocationEvent {
  type: typeof ClientEventType.SELECT_LOCATION;
  location_id: number;
}

/** Contact details given to the pre-chat form, before the conversation exists (CAIV-362). */
export interface SubmitPreChatFormEvent {
  type: typeof ClientEventType.SUBMIT_PRECHAT_FORM;
  details: Record<string, unknown>;
}

export type ClientEvent =
  | UserMessageEvent
  | ToolResponseEvent
  | SubmitPreChatFormEvent
  | PingEvent
  | RestoreSessionEvent
  | UpdateLanguageEvent
  | SelectLocationEvent;

// ============================================================================
// Server -> Client Event Data Types
// ============================================================================

export interface SessionReadyData {
  session_id: string;
  router_id: number;
}

export interface SessionRestoredData {
  session_id: string;
  old_session_id: string;
  conversation_id: string | null;
  router_id: number;
  /** Set when the restored conversation is already scoped, so the picker stays away. */
  location_id?: number | null;
}

/**
 * What to do about a location before the first message, decided server-side (CAIV-677).
 *
 * `fixed` means the server already knows and has written it; the widget shows nothing.
 * `ask` carries the shortlist to offer. `none` means there is nothing worth asking.
 */
export interface LocationScope {
  mode: 'fixed' | 'ask' | 'none';
  location_id: number | null;
  locations: Array<{ id: string; name: string }>;
  /**
   * Whether the visitor may correct the club they picked. Only meaningful for `ask`, where they
   * were asked in the first place; the server refuses a change when this is false, so the widget
   * hides the affordance rather than offering a button that does nothing.
   */
  can_change?: boolean;
}

export interface ChatSettingsMessages {
  welcome?: string;
  placeholder?: string;
  offline?: string;
  error?: string;
}

/**
 * The contact form a club asks for before the chat starts (CAIV-362).
 *
 * Server-decided: the widget renders what this says and nothing more. `locations` is populated
 * only when the club has more than one site, in which case the form carries the club question
 * itself and the standalone picker stands down.
 */
export interface PreChatForm {
  /** The question shown with the form, in the club's language. */
  prompt: string;
  title: string;
  fields: Record<string, { type: string; title: string; format?: string; enum?: string[] }>;
  required: string[];
  locations: Array<{ id: string; name: string }>;
  /** Whether this visitor has already answered. Sent either way so the card can lock. */
  satisfied: boolean;
}

/** What a visitor typed into the pre-chat form. */
export interface PreChatAnswer {
  first_name: string;
  mobile_phone: string;
  email: string;
  location_id?: string;
}

export interface ChatSettingsData {
  router_id: number;
  name: string;
  label: string;
  language?: string;
  sales_starter?: string;
  primary_color?: string;
  secondary_color?: string;
  accent_color?: string;
  background_color?: string;
  text_color?: string;
  user_text_color?: string;
  messages?: ChatSettingsMessages;
  location_scope?: LocationScope;
  /** The handoff banner, when there is one to show right now (CAIV-1063). */
  whatsapp_handoff?: WhatsAppHandoff;
  prechat_form?: PreChatForm;
}

export interface StreamingChunkData {
  message_id: string;
  chunk: string;
  is_first: boolean;
}

export interface StreamCompleteData {
  message_id: string;
  content: string;
  metadata: Record<string, unknown>;
}

export interface NewMessageData {
  message_id: string;
  content: string;
  sender_type: 'user' | 'assistant' | 'system';
  metadata: Record<string, unknown>;
}

export type ToolUiType =
  | 'date_picker'
  | 'slot_selection'
  | 'location_picker'
  | 'user_form'
  | 'appointment_picker';

export interface ToolUiMetadata {
  tool_call_id: string;
  tool_name: string;
  ui_type: ToolUiType;
  ui_config?: Record<string, unknown>;
}

/**
 * A "continue on WhatsApp" affordance (CAIV-1063).
 *
 * The club's number is not in it: it is resolved server-side when the link is followed.
 * That matters because a visitor can correct which club they are talking to
 * mid-conversation, so a number captured earlier could be the previous club's.
 */
export interface WhatsAppHandoff {
  code: string;
  /**
   * Absolute, because the widget runs on the club's own website: a relative path would
   * resolve against their domain. Built server-side from SITE_URL.
   */
  url: string;
}

export interface LanguageUpdateData {
  language: string;
  session_id: string | null;
}

export interface PongData {
  timestamp?: number;
}

export interface ErrorData {
  message: string;
  code?: string;
}

// ============================================================================
// Server -> Client Event Types
// ============================================================================

export interface SessionReadyEvent {
  type: typeof ServerEventType.SESSION_READY;
  data: SessionReadyData;
}

export interface SessionRestoredEvent {
  type: typeof ServerEventType.SESSION_RESTORED;
  data: SessionRestoredData;
}

export interface ChatSettingsEvent {
  type: typeof ServerEventType.CHAT_SETTINGS;
  data: ChatSettingsData;
}

export interface StreamingChunkEvent {
  type: typeof ServerEventType.STREAMING_CHUNK;
  data: StreamingChunkData;
}

export interface StreamCompleteEvent {
  type: typeof ServerEventType.STREAM_COMPLETE;
  data: StreamCompleteData;
}

export interface NewMessageEvent {
  type: typeof ServerEventType.NEW_MESSAGE;
  data: NewMessageData;
}

export interface LanguageUpdateEvent {
  type: typeof ServerEventType.LANGUAGE_UPDATE;
  data: LanguageUpdateData;
}

export interface PongEvent {
  type: typeof ServerEventType.PONG;
  data: PongData;
}

export interface ErrorEvent {
  type: typeof ServerEventType.ERROR;
  data: ErrorData;
}

/**
 * The handoff banner, pushed between chat_settings (CAIV-1063): once when the
 * conversation first exists, and after a turn that started a new attention episode.
 */
export interface WhatsAppHandoffEvent {
  type: typeof ServerEventType.WHATSAPP_HANDOFF;
  /** `raise` re-opens a banner the visitor dismissed: the assistant just failed them. */
  data: WhatsAppHandoff & { raise: boolean };
}

export type ServerEvent =
  | SessionReadyEvent
  | SessionRestoredEvent
  | ChatSettingsEvent
  | StreamingChunkEvent
  | StreamCompleteEvent
  | NewMessageEvent
  | LanguageUpdateEvent
  | PongEvent
  | ErrorEvent
  | WhatsAppHandoffEvent;

// ============================================================================
// Message Types for UI
// ============================================================================

export type MessageSenderType = 'user' | 'assistant' | 'system';

export interface Message {
  id: string;
  content: string;
  senderType: MessageSenderType;
  timestamp: Date;
  isStreaming?: boolean;
  metadata?: Record<string, unknown> & {
    tool_ui?: ToolUiMetadata;
  };
}

// ============================================================================
// Widget Configuration Types
// ============================================================================

export type WidgetPosition = 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';

export interface WidgetConfig {
  /** Channel ID (UUID) — preferred identifier for the widget channel */
  channelId?: string;
  /** Router ID (numeric) — legacy identifier, kept for backward compatibility */
  routerId: string;
  apiHost: string;
  wsHost: string;
  position?: WidgetPosition;
  language?: string;
  /** Overrides the channel's layout direction, the way `position` overrides its corner. */
  direction?: 'ltr' | 'rtl';
}
